import type { Ref } from "react";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { formatNOK } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";

/** Data-drevet sjekkpunkt (grønt/rødt) med valgfri handlingslenke. VIB-1308-uttrekk. */
export function DataChecklistRow({
  label,
  blocked,
  blockedMsg,
  action,
}: {
  label: string;
  blocked: boolean;
  blockedMsg: string;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded border px-3 py-2.5",
        blocked ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50",
      )}
    >
      <div
        className={cn(
          "mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0",
          blocked ? "border-red-300 bg-white" : "bg-emerald-500 border-emerald-500 text-white",
        )}
      >
        {blocked ? <SrIcon name="alert-triangle" className="w-3 h-3 text-red-500" /> : <SrIcon name="check" className="w-3.5 h-3.5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className={cn("text-sm", !blocked && "line-through text-muted-foreground")}>{label}</div>
        {blocked && (
          <div className="mt-0.5 text-xs text-red-700 flex items-center gap-2">
            <span>{blockedMsg}</span>
            {action && (
              <button
                type="button"
                onClick={action.onClick}
                className="inline-flex items-center gap-1 text-red-700 hover:text-red-900 underline"
              >
                {action.label}
                <SrIcon name="external-link" className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Manuelt avkryssbart sjekkpunkt med valgfritt underinnhold. */
export function ManualChecklistRow({
  label,
  checked,
  onToggle,
  variant = "default",
  children,
  checkboxRef,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
  variant?: "default" | "warning";
  children?: React.ReactNode;
  /** Lar dialogen sette startfokus på dette punktet (VIB-2529). */
  checkboxRef?: Ref<HTMLButtonElement>;
}) {
  return (
    <label
      className={cn(
        "flex items-start gap-3 rounded border px-3 py-2.5 cursor-pointer group",
        variant === "warning" ? "border-amber-200 bg-amber-50" : "border-gray-200 bg-white",
      )}
    >
      {/* Ekte checkbox så Tab/Space virker (VIB-2529). Label-wrapperen gir klikk på teksten. */}
      <Checkbox
        ref={checkboxRef}
        checked={checked}
        onCheckedChange={onToggle}
        aria-label={label}
        className="mt-0.5 h-5 w-5 rounded border-2 border-gray-300 bg-white group-hover:border-emerald-400 data-[state=checked]:border-emerald-500 data-[state=checked]:bg-emerald-500 data-[state=checked]:text-white"
      />
      <div className="flex-1 min-w-0">
        <div className={cn("text-sm", checked && "line-through text-muted-foreground")}>{label}</div>
        {children}
      </div>
    </label>
  );
}

/** BF estimert/faktisk/differanse-blokk vist under BF-sjekkpunktet. */
export function BfReviewBlock({
  bfEstimated,
  bfActual,
  bfDiff,
}: {
  bfEstimated: number;
  bfActual: number | null;
  bfDiff: number | null;
}) {
  return (
    <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
      <div className="rounded bg-gray-50 px-2 py-1.5">
        <div className="text-muted-foreground">Estimert GEEK</div>
        <div className="font-medium tabular-nums">{formatNOK(bfEstimated)}</div>
      </div>
      <div className="rounded bg-gray-50 px-2 py-1.5">
        <div className="text-muted-foreground">Faktisk GEEK</div>
        <div className="font-medium tabular-nums">{bfActual != null ? formatNOK(bfActual) : "—"}</div>
      </div>
      <div className="rounded bg-gray-50 px-2 py-1.5">
        <div className="text-muted-foreground">Differanse</div>
        <div
          className={cn(
            "font-medium tabular-nums",
            bfDiff != null && bfDiff < 0 && "text-red-600",
            bfDiff != null && bfDiff > 0 && "text-emerald-700",
          )}
        >
          {bfDiff != null ? formatNOK(bfDiff) : "—"}
        </div>
      </div>
    </div>
  );
}
